import {
  getInvoicePoSkuMatchingResultAPI,
  type AiGatewayFallbackErrorType,
  type InvoicePoMatchingsResponseType,
  type SkuMatchingMatchType,
  type SkuMatchingResultType,
  type SkuMatchingStatusType,
  type TriggerSkuMatchingApiResponseType,
} from 'features/Enterprise/Vendor/services/vendorInvoice'

import type { SkuMatchLineType, SkuMatchRowType, SkuMatchTabType } from './skuMatchTypes'

const SKU_MATCHING_POLL_INTERVAL_MS = 2000
const SKU_MATCHING_MAX_POLL_ATTEMPTS = 60
const SKU_MATCHING_FALLBACK_ERROR_MESSAGE = 'Gagal melakukan pencocokan SKU'

const sleep = (durationMs: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, durationMs)
  })

/**
 * Detect a graceful-failure envelope in a 200 response.
 *
 * `ai-be` (AI Gateway) is an optional dependency, so Commercial-BE swallows its errors
 * and still answers HTTP 200 with an `error` object (code 13) instead of a 500. An
 * error that carries a message always wins over `data`, even if `data` also happens to
 * be present, so callers must not chain to the next API call.
 */
export const hasSkuMatchingFallbackError = (
  response: { error?: AiGatewayFallbackErrorType } | undefined,
): boolean => {
  const error = response?.error
  if (error == null) {
    return false
  }
  return error.status === true || (error.message ?? '').trim().length > 0
}

/**
 * Read `request_id` from a trigger response, or `null` when the response reports a
 * failure so the caller never starts polling.
 */
export const resolveSkuMatchingTriggerRequestId = (
  triggerResponse: TriggerSkuMatchingApiResponseType | undefined,
): string | null => {
  if (hasSkuMatchingFallbackError(triggerResponse)) {
    return null
  }
  return triggerResponse?.data?.request_id ?? null
}

export const buildSkuMatchingPollFailedResult = (requestId: string): SkuMatchingResultType => ({
  request_id: requestId,
  status: 'FAILED',
  matches: [],
  error_message: SKU_MATCHING_FALLBACK_ERROR_MESSAGE,
})

/**
 * Narrow an unknown `status` to one the FE knows.
 *
 * HTTP 200 does not guarantee a valid payload, so an unrecognized status must stop the
 * poll loop and fall back instead of being treated as `PROCESSING`, which would just
 * retry until the attempt budget runs out.
 */
export const isRecognizedSkuMatchingStatus = (value: unknown): value is SkuMatchingStatusType =>
  value === 'SUCCESS' || value === 'PROCESSING' || value === 'FAILED'

const pollInvoicePoSkuMatchingResultAttempt = async (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  requestId: string,
  attempt: number,
): Promise<SkuMatchingResultType> => {
  if (attempt >= SKU_MATCHING_MAX_POLL_ATTEMPTS) {
    return buildSkuMatchingPollFailedResult(requestId)
  }

  const { data } = await getInvoicePoSkuMatchingResultAPI(
    invoiceId,
    invoicePurchaseOrderId,
    requestId,
  )
  const result = data?.data

  // 200 with an error envelope, or a payload we do not understand: stop the chain,
  // do not retry, do not trust the matches.
  if (
    hasSkuMatchingFallbackError(data) ||
    result == null ||
    !isRecognizedSkuMatchingStatus(result.status)
  ) {
    return buildSkuMatchingPollFailedResult(requestId)
  }

  if (result.status === 'SUCCESS' || result.status === 'FAILED') {
    return result
  }

  await sleep(SKU_MATCHING_POLL_INTERVAL_MS)
  return pollInvoicePoSkuMatchingResultAttempt(
    invoiceId,
    invoicePurchaseOrderId,
    requestId,
    attempt + 1,
  )
}

export const pollInvoicePoSkuMatchingResult = (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  requestId: string,
): Promise<SkuMatchingResultType> =>
  pollInvoicePoSkuMatchingResultAttempt(invoiceId, invoicePurchaseOrderId, requestId, 0)

export function derivePickMatchListsFromRows(rows: SkuMatchRowType[]): {
  invoiceLines: SkuMatchLineType[]
  unmatchedInvoiceLines: SkuMatchLineType[]
  poLines: SkuMatchLineType[]
} {
  const { invoiceLines } = rows.reduce<{
    seenInvoiceIds: Set<string>
    invoiceLines: SkuMatchLineType[]
  }>(
    (accumulator, row) => {
      if (row.invoice != null && !accumulator.seenInvoiceIds.has(row.invoice.id)) {
        accumulator.seenInvoiceIds.add(row.invoice.id)
        accumulator.invoiceLines.push(row.invoice)
      }
      return accumulator
    },
    { seenInvoiceIds: new Set<string>(), invoiceLines: [] },
  )

  const unmatchedInvoiceLines = rows
    .filter((row) => row.invoice != null && !row.linked)
    .map((row) => row.invoice as SkuMatchLineType)

  const { poLines } = rows.reduce<{
    seenPoIds: Set<string>
    poLines: SkuMatchLineType[]
  }>(
    (accumulator, row) => {
      if (row.po != null && !accumulator.seenPoIds.has(row.po.id)) {
        accumulator.seenPoIds.add(row.po.id)
        accumulator.poLines.push(row.po)
      }
      return accumulator
    },
    { seenPoIds: new Set<string>(), poLines: [] },
  )

  return { invoiceLines, unmatchedInvoiceLines, poLines }
}

export function sortPoLinesByInvoiceOrder(
  poLines: SkuMatchLineType[],
  pendingMappings: Record<string, string>,
  invoiceLines: SkuMatchLineType[],
  originalPoIndexById: Map<string, number>,
): SkuMatchLineType[] {
  const invoiceIndexById = new Map(invoiceLines.map((line, idx) => [line.id, idx] as const))

  const getOriginalIndex = (poId: string): number => originalPoIndexById.get(poId) ?? 0

  return [...poLines].sort((firstPo, secondPo) => {
    const firstInvoiceId = pendingMappings[firstPo.id]
    const secondInvoiceId = pendingMappings[secondPo.id]
    const firstMapped = firstInvoiceId != null && firstInvoiceId !== ''
    const secondMapped = secondInvoiceId != null && secondInvoiceId !== ''

    if (firstMapped && secondMapped) {
      const firstIndex = invoiceIndexById.get(firstInvoiceId) ?? Number.MAX_SAFE_INTEGER
      const secondIndex = invoiceIndexById.get(secondInvoiceId) ?? Number.MAX_SAFE_INTEGER
      if (firstIndex !== secondIndex) {
        return firstIndex - secondIndex
      }
      return getOriginalIndex(firstPo.id) - getOriginalIndex(secondPo.id)
    }
    if (firstMapped && !secondMapped) {
      return -1
    }
    if (!firstMapped && secondMapped) {
      return 1
    }
    return getOriginalIndex(firstPo.id) - getOriginalIndex(secondPo.id)
  })
}

export function buildDefaultPendingMappingsForTab(
  tab: SkuMatchTabType | null,
): Record<string, string> {
  if (tab == null) {
    return {}
  }
  return Object.fromEntries(
    tab.rows
      .filter(
        (row): row is SkuMatchRowType & { invoice: SkuMatchLineType; po: SkuMatchLineType } =>
          row.invoice != null && row.po != null,
      )
      .map((row) => [row.po.id, row.invoice.id] as const),
  )
}

export const buildInitialPendingMappingsForTab = (
  tab: SkuMatchTabType | null,
  aiMappings: Record<string, string> = {},
) => ({ ...buildDefaultPendingMappingsForTab(tab), ...aiMappings })

export const resolvePendingMappingsForPo = ({
  tab,
  purchaseOrderId,
  pendingMappingsByPo,
  aiMappingsByPo,
}: {
  tab: SkuMatchTabType | null
  purchaseOrderId: number
  pendingMappingsByPo: Record<number, Record<string, string>>
  aiMappingsByPo: Record<number, Record<string, string>>
}) =>
  pendingMappingsByPo[purchaseOrderId] ??
  buildInitialPendingMappingsForTab(tab, aiMappingsByPo[purchaseOrderId] ?? {})

type SkuMatchTabInputType = Omit<
  SkuMatchTabType,
  'invoiceLines' | 'unmatchedInvoiceLines' | 'poLines'
> & {
  invoiceLines?: SkuMatchLineType[]
  poLines?: SkuMatchLineType[]
}

export const createSkuMatchTab = (input: SkuMatchTabInputType): SkuMatchTabType => {
  const derived = derivePickMatchListsFromRows(input.rows)
  return {
    ...input,
    invoiceLines: input.invoiceLines ?? derived.invoiceLines,
    unmatchedInvoiceLines: derived.unmatchedInvoiceLines,
    poLines: input.poLines ?? derived.poLines,
  }
}

export function rebuildTabFromRows(tab: SkuMatchTabType, rows: SkuMatchRowType[]): SkuMatchTabType {
  const { invoiceLines, unmatchedInvoiceLines, poLines } = derivePickMatchListsFromRows(rows)
  return {
    ...tab,
    rows,
    invoiceLines,
    unmatchedInvoiceLines,
    poLines,
    invoiceHeaderCount: invoiceLines.length,
    poHeaderCount: poLines.length,
  }
}

export function buildPendingMappingsFromAiMatches(
  matches: SkuMatchingMatchType[],
): Record<string, string> {
  const pendingMappings: Record<string, string> = {}
  matches.forEach(({ invoice_item_id, po_item_ids }) => {
    po_item_ids.forEach((poItemId) => {
      pendingMappings[String(poItemId)] = String(invoice_item_id)
    })
  })
  return pendingMappings
}

export const hasUnmatchedInvoiceItemsInMatchingsApi = (
  matchings: InvoicePoMatchingsResponseType,
): boolean => matchings.invoice_items.some((item) => item.po_item_ids.length === 0)

export const hasUnmatchedPoItemsInMatchingsApi = (
  matchings: InvoicePoMatchingsResponseType,
): boolean => matchings.po_items.some((item) => item.invoice_po_item_ids.length === 0)

export const shouldRunSkuMatchingForMatchingsApi = (
  matchings: InvoicePoMatchingsResponseType,
): boolean =>
  matchings.invoice_items.length > 0 &&
  matchings.po_items.length > 0 &&
  (hasUnmatchedInvoiceItemsInMatchingsApi(matchings) ||
    hasUnmatchedPoItemsInMatchingsApi(matchings))

export const buildSkuMatchPanelAlertsFromPendingMappings = (
  activeTab: SkuMatchTabType | null,
  pendingMappings: Record<string, string>,
) => {
  if (!activeTab || (activeTab.invoiceLines.length === 0 && activeTab.poLines.length === 0)) {
    return { hasPanelData: false, showInvoiceAlert: false, showPoAlert: false }
  }
  const matchedInvoiceIds = new Set(Object.values(pendingMappings))
  const matchedPoIds = new Set(Object.keys(pendingMappings))
  return {
    hasPanelData: true,
    showInvoiceAlert: activeTab.invoiceLines.some((line) => !matchedInvoiceIds.has(line.id)),
    showPoAlert: activeTab.poLines.some((line) => !matchedPoIds.has(line.id)),
  }
}

export const buildSkuMatchPanelAlertsForActiveTab = ({
  activeTab,
  pendingMappings,
  aiMappingsByPo,
  isSkuMatchLoadComplete,
}: {
  activeTab: SkuMatchTabType | null
  pendingMappings: Record<string, string>
  aiMappingsByPo: Record<number, Record<string, string>>
  isSkuMatchLoadComplete: boolean
}) => {
  if (!isSkuMatchLoadComplete) {
    return { hasPanelData: false, showInvoiceAlert: false, showPoAlert: false }
  }

  const purchaseOrderId = activeTab ? Number(activeTab.id) : NaN
  const defaultMappings = buildDefaultPendingMappingsForTab(activeTab)
  const aiMappings = Number.isFinite(purchaseOrderId) ? aiMappingsByPo[purchaseOrderId] ?? {} : {}
  const effectiveMappings = { ...defaultMappings, ...aiMappings, ...pendingMappings }

  return buildSkuMatchPanelAlertsFromPendingMappings(activeTab, effectiveMappings)
}

export const filterAiMappingsExcludingExistingMatchings = (
  aiMappings: Record<string, string>,
  matchings: InvoicePoMatchingsResponseType,
): Record<string, string> => {
  const linkedPoItemIds = new Set(
    matchings.po_items
      .filter((poItem) => poItem.invoice_po_item_ids.length > 0)
      .map((poItem) => String(poItem.purchase_order_item_id)),
  )
  return Object.fromEntries(
    Object.entries(aiMappings).filter(([poItemId]) => !linkedPoItemIds.has(poItemId)),
  )
}

export function buildPendingMappingsFromAiMatchesForUnmatchedItems(
  matches: SkuMatchingMatchType[],
  matchings: InvoicePoMatchingsResponseType,
): Record<string, string> {
  return filterAiMappingsExcludingExistingMatchings(
    buildPendingMappingsFromAiMatches(matches),
    matchings,
  )
}

export function buildMatchingsPayload(pendingMappings: Record<string, string>) {
  const byInvoice: Record<string, number[]> = {}
  Object.entries(pendingMappings).forEach(([poItemId, invoiceItemId]) => {
    if (!invoiceItemId) {
      return
    }
    if (!byInvoice[invoiceItemId]) {
      byInvoice[invoiceItemId] = []
    }
    byInvoice[invoiceItemId].push(Number(poItemId))
  })
  return Object.entries(byInvoice).map(([invoiceItemId, poItemIds]) => ({
    invoice_item_id: Number(invoiceItemId),
    po_item_ids: poItemIds,
  }))
}

const normalizeSkuCode = (code: string): string => code.trim().toLowerCase()

function autoMatchUnmatchedItemsBySkuCode(
  matchings: InvoicePoMatchingsResponseType,
  matchingsByInvoiceId: Map<number, Set<number>>,
  onlyInvoiceItemIds?: Set<number>,
): boolean {
  const usedPoItemIds = new Set<number>()
  matchingsByInvoiceId.forEach((poItemIds) => {
    poItemIds.forEach((poItemId) => usedPoItemIds.add(poItemId))
  })

  const unmatchedPoItemIdsBySkuCode = new Map<string, number[]>()
  matchings.po_items.forEach((poItem) => {
    if (poItem.invoice_po_item_ids.length > 0) {
      return
    }
    if (usedPoItemIds.has(poItem.purchase_order_item_id)) {
      return
    }
    const skuCode = normalizeSkuCode(poItem.sku_code)
    if (!skuCode) {
      return
    }
    const poItemIds = unmatchedPoItemIdsBySkuCode.get(skuCode) ?? []
    poItemIds.push(poItem.purchase_order_item_id)
    unmatchedPoItemIdsBySkuCode.set(skuCode, poItemIds)
  })

  let hasNewMatches = false
  matchings.invoice_items.forEach((invoiceItem) => {
    if (onlyInvoiceItemIds && !onlyInvoiceItemIds.has(invoiceItem.invoice_po_item_id)) {
      return
    }
    if (invoiceItem.po_item_ids.length > 0) {
      return
    }
    if (matchingsByInvoiceId.has(invoiceItem.invoice_po_item_id)) {
      return
    }
    const skuCode = normalizeSkuCode(invoiceItem.vendor_sku_code)
    if (!skuCode) {
      return
    }
    const candidates = unmatchedPoItemIdsBySkuCode.get(skuCode)
    if (!candidates || candidates.length === 0) {
      return
    }
    const poItemId = candidates.shift() as number
    matchingsByInvoiceId.set(invoiceItem.invoice_po_item_id, new Set([poItemId]))
    hasNewMatches = true
  })

  return hasNewMatches
}

const collectExistingMatchingsByInvoiceId = (
  matchings: InvoicePoMatchingsResponseType,
): Map<number, Set<number>> => {
  const matchingsByInvoiceId = new Map<number, Set<number>>()

  matchings.invoice_items.forEach((invoiceItem) => {
    if (invoiceItem.po_item_ids.length > 0) {
      matchingsByInvoiceId.set(invoiceItem.invoice_po_item_id, new Set(invoiceItem.po_item_ids))
    }
  })

  matchings.po_items.forEach((poItem) => {
    poItem.invoice_po_item_ids.forEach((invoicePoItemId) => {
      let poItemIds = matchingsByInvoiceId.get(invoicePoItemId)
      if (!poItemIds) {
        poItemIds = new Set()
        matchingsByInvoiceId.set(invoicePoItemId, poItemIds)
      }
      poItemIds.add(poItem.purchase_order_item_id)
    })
  })

  return matchingsByInvoiceId
}

export function buildAutoConnectMatchingsPayload(
  matchings: InvoicePoMatchingsResponseType,
  options?: { onlyInvoiceItemIds?: Set<number> },
): {
  matchings: { invoice_item_id: number; po_item_ids: number[] }[]
  hasNewMatches: boolean
} {
  const matchingsByInvoiceId = collectExistingMatchingsByInvoiceId(matchings)
  const hasNewSkuCodeMatches = autoMatchUnmatchedItemsBySkuCode(
    matchings,
    matchingsByInvoiceId,
    options?.onlyInvoiceItemIds,
  )

  const result = Array.from(matchingsByInvoiceId.entries()).map(([invoiceItemId, poItemIds]) => ({
    invoice_item_id: invoiceItemId,
    po_item_ids: Array.from(poItemIds),
  }))

  return { matchings: result, hasNewMatches: hasNewSkuCodeMatches }
}

export const areMappingsEqual = (
  firstMappings: Record<string, string>,
  secondMappings: Record<string, string>,
): boolean => {
  const firstKeys = Object.keys(firstMappings)
  const secondKeys = Object.keys(secondMappings)
  if (firstKeys.length !== secondKeys.length) {
    return false
  }
  return firstKeys.every((key) => firstMappings[key] === secondMappings[key])
}

export const lineMatchesQuery = (line: SkuMatchLineType, query: string) => {
  const needle = query.trim().toLowerCase()
  if (!needle) {
    return true
  }
  return (
    line.productName.toLowerCase().includes(needle) ||
    line.productCode.toLowerCase().includes(needle)
  )
}

export const extractPoNumberFromTabLabel = (label: string) => label.split(' - ')[0]?.trim() ?? label

export const sanitizePendingMappingsForTab = (
  tab: SkuMatchTabType | null,
  pendingMappings: Record<string, string>,
): Record<string, string> => {
  if (!tab) {
    return {}
  }
  const invoiceIdSet = new Set(tab.invoiceLines.map((line) => line.id))
  const poIdSet = new Set(tab.poLines.map((line) => line.id))
  return Object.fromEntries(
    Object.entries(pendingMappings).filter(
      ([poId, invoiceId]) => poIdSet.has(poId) && invoiceId !== '' && invoiceIdSet.has(invoiceId),
    ),
  )
}

const isMatchableProductCode = (productCode: string): boolean => {
  const normalizedProductCode = normalizeSkuCode(productCode)
  return normalizedProductCode.length > 0 && normalizedProductCode !== '-'
}

const buildTargetProductCodeSet = (productCodes: string[]): Set<string> | null => {
  if (productCodes.length === 0) {
    return null
  }
  return new Set(
    productCodes
      .map((productCode) => normalizeSkuCode(productCode))
      .filter((productCode) => isMatchableProductCode(productCode)),
  )
}

const isProductCodeIncluded = (
  productCode: string,
  targetProductCodes: Set<string> | null,
): boolean =>
  isMatchableProductCode(productCode) &&
  (targetProductCodes == null || targetProductCodes.has(normalizeSkuCode(productCode)))

const buildPoLinesByProductCode = (
  poLines: SkuMatchLineType[],
  mappedPoIds: Set<string>,
  targetProductCodes: Set<string> | null,
): Map<string, SkuMatchLineType[]> =>
  poLines.reduce<Map<string, SkuMatchLineType[]>>((accumulator, poLine) => {
    if (
      mappedPoIds.has(poLine.id) ||
      !isProductCodeIncluded(poLine.productCode, targetProductCodes)
    ) {
      return accumulator
    }
    const productCode = normalizeSkuCode(poLine.productCode)
    const groupedPoLines = accumulator.get(productCode) ?? []
    groupedPoLines.push(poLine)
    accumulator.set(productCode, groupedPoLines)
    return accumulator
  }, new Map())

const buildInvoiceProductCodeMappings = (
  invoiceLines: SkuMatchLineType[],
  poLinesByProductCode: Map<string, SkuMatchLineType[]>,
  mappedInvoiceIds: Set<string>,
  targetProductCodes: Set<string> | null,
): Record<string, string> => {
  const nextPoLineIndexByProductCode = new Map<string, number>()
  const productCodeMappings: Record<string, string> = {}

  invoiceLines.forEach((invoiceLine) => {
    if (
      mappedInvoiceIds.has(invoiceLine.id) ||
      !isProductCodeIncluded(invoiceLine.productCode, targetProductCodes)
    ) {
      return
    }
    const productCode = normalizeSkuCode(invoiceLine.productCode)
    const candidatePoLines = poLinesByProductCode.get(productCode)
    if (!candidatePoLines || candidatePoLines.length === 0) {
      return
    }
    const nextIndex = nextPoLineIndexByProductCode.get(productCode) ?? 0
    const poLine = candidatePoLines[nextIndex]
    if (!poLine) {
      return
    }
    nextPoLineIndexByProductCode.set(productCode, nextIndex + 1)
    productCodeMappings[poLine.id] = invoiceLine.id
    mappedInvoiceIds.add(invoiceLine.id)
  })

  return productCodeMappings
}

export const buildProductCodePendingMappingsForTab = (
  tab: SkuMatchTabType | null,
  existingMappings: Record<string, string> = {},
  productCodes: string[] = [],
): Record<string, string> => {
  if (!tab) {
    return {}
  }

  const targetProductCodes = buildTargetProductCodeSet(productCodes)
  const mappedInvoiceIds = new Set(Object.values(existingMappings))
  const mappedPoIds = new Set(Object.keys(existingMappings))
  const poLinesByProductCode = buildPoLinesByProductCode(
    tab.poLines,
    mappedPoIds,
    targetProductCodes,
  )

  return buildInvoiceProductCodeMappings(
    tab.invoiceLines,
    poLinesByProductCode,
    mappedInvoiceIds,
    targetProductCodes,
  )
}

export const mergePendingMappingsPreservingExisting = (
  tab: SkuMatchTabType | null,
  previousMappings: Record<string, string>,
  apiDefaults: Record<string, string>,
): Record<string, string> => {
  const preserved = sanitizePendingMappingsForTab(tab, previousMappings)
  const mappedInvoiceIds = new Set(Object.values(preserved))
  const nextMappings = { ...preserved }

  Object.entries(apiDefaults).forEach(([poId, invoiceId]) => {
    if (nextMappings[poId] != null) {
      return
    }
    if (mappedInvoiceIds.has(invoiceId)) {
      return
    }
    nextMappings[poId] = invoiceId
    mappedInvoiceIds.add(invoiceId)
  })

  return nextMappings
}

export const mergePendingMappingsAfterManualSkuAdd = (
  tab: SkuMatchTabType | null,
  previousMappings: Record<string, string>,
  apiDefaults: Record<string, string>,
  addedProductCodes: string[],
): Record<string, string> => {
  const mergedMappings = mergePendingMappingsPreservingExisting(tab, previousMappings, apiDefaults)
  if (addedProductCodes.length === 0) {
    return mergedMappings
  }
  return {
    ...mergedMappings,
    ...buildProductCodePendingMappingsForTab(tab, mergedMappings, addedProductCodes),
  }
}

export const countMatchedInvoiceSkusInTab = (
  tab: SkuMatchTabType,
  pendingMappings: Record<string, string>,
) => {
  const total = tab.invoiceLines.length
  if (total === 0) {
    return { matched: 0, total: 0 }
  }

  const invoiceIdSet = new Set(tab.invoiceLines.map((line) => line.id))
  const poIdSet = new Set(tab.poLines.map((poLine) => poLine.id))
  const matchedInvoiceIds = new Set<string>()
  Object.entries(pendingMappings).forEach(([poId, invoiceId]) => {
    if (poIdSet.has(poId) && invoiceId !== '' && invoiceIdSet.has(invoiceId)) {
      matchedInvoiceIds.add(invoiceId)
    }
  })

  if (matchedInvoiceIds.size > 0) {
    return { matched: matchedInvoiceIds.size, total }
  }

  const matchedFromConnections = tab.invoiceLines.filter((line) => line.connections > 0).length
  return { matched: matchedFromConnections, total }
}

export const buildPoTabCountLabels = (
  tabs: SkuMatchTabType[],
  pendingMappings: Record<string, string>,
  activeTabId: string | undefined,
  pendingMappingsByPo: Record<number, Record<string, string>>,
  aiMappingsByPo: Record<number, Record<string, string>>,
) =>
  tabs.map((tab) => {
    const purchaseOrderId = Number(tab.id)
    const tabMappings =
      activeTabId === tab.id
        ? pendingMappings
        : resolvePendingMappingsForPo({
            tab,
            purchaseOrderId,
            pendingMappingsByPo,
            aiMappingsByPo,
          })
    const { matched } = countMatchedInvoiceSkusInTab(tab, tabMappings)
    const poNumber = extractPoNumberFromTabLabel(tab.label)
    return `${poNumber} - ${matched}/${tab.poLines.length}`
  })

export const buildActiveTabCounts = (
  activeTab: SkuMatchTabType | null,
  pendingMappings: Record<string, string>,
) => {
  if (!activeTab) {
    return { activeTabPoNumber: '', activeTabMatchedCount: 0, activeTabTotalCount: 0 }
  }
  const { matched, total } = countMatchedInvoiceSkusInTab(activeTab, pendingMappings)
  return {
    activeTabPoNumber: extractPoNumberFromTabLabel(activeTab.label),
    activeTabMatchedCount: matched,
    activeTabTotalCount: total,
  }
}

export const buildOtherPoTabOptions = (tabs: SkuMatchTabType[], activeTabIndex: number) =>
  tabs
    .map((tab, index) => ({
      id: tab.id,
      label: extractPoNumberFromTabLabel(tab.label),
      index,
    }))
    .filter((tabOption) => tabOption.index !== activeTabIndex)
    .map(({ id, label }) => ({ id, label }))

export const filterUnmatchedInvoiceLines = (
  activeTab: SkuMatchTabType | null,
  invoiceSearch: string,
) => {
  if (!activeTab) {
    return []
  }
  return activeTab.invoiceLines.filter((line) => lineMatchesQuery(line, invoiceSearch))
}

export const filterPoLines = (activeTab: SkuMatchTabType | null, poSearch: string) => {
  if (!activeTab) {
    return []
  }
  return activeTab.poLines.filter((line) => lineMatchesQuery(line, poSearch))
}

export type SkuMatchTabLoadStateType = 'loading' | 'empty' | 'ready' | 'error'

export const resolveSkuMatchTabLoadState = ({
  purchaseOrderId,
  skuMatchData,
  skuMatchFetchFailed,
  isLoading,
  hasRows,
}: {
  purchaseOrderId: number | undefined
  skuMatchData: Record<number, unknown>
  skuMatchFetchFailed: Record<number, boolean>
  isLoading: boolean
  hasRows: boolean
}): SkuMatchTabLoadStateType => {
  if (purchaseOrderId == null) {
    return 'loading'
  }
  if (skuMatchFetchFailed[purchaseOrderId]) {
    return 'error'
  }
  if (!skuMatchData[purchaseOrderId]) {
    return 'loading'
  }
  if (hasRows) {
    return 'ready'
  }
  if (isLoading) {
    return 'loading'
  }
  return 'empty'
}
